import { useState, useEffect, useMemo } from 'react'
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@cutilei/ui'
import { DateTime } from 'luxon'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { getApiErrorMessage, logApiError } from 'services/apiError'
import Select from 'components/Inputs/Select'
import MoneyInput from 'components/Inputs/MoneyInput'
import DateInput from 'components/Inputs/DateInput'
import { ReactComponent as ButtonLoading } from 'icons/loading.svg'

import {
  Form,
  FormField,
  Input,
  Button,
  LinkButton,
  Row,
  Label,
  CheckboxLabel,
  InfoText,
  ErrorContainer
} from './styles'
import CheckBox from 'components/CheckBox'

const KIND_OPTIONS = [
  { label: 'Fixa', value: 'fixed' },
  { label: 'Variável', value: 'variable' }
]

const KIND_LABELS = { fixed: 'Fixa', variable: 'Variável' }

const categoryOption = category => ({
  label: `${category.name} (${KIND_LABELS[category.kind]})`,
  value: category
})

export default function DreExpenseModal ({
  visible,
  year,
  expense = null,
  categories = [],
  onConfirm: handleConfirm,
  onCategoryCreated: handleCategoryCreated = () => {},
  onClose: handleClose
}) {
  const businessId = AuthService.getBusinessId ()
  const requestAuth = AuthService.getRequestAuth ()
  const [category, setCategory] = useState (null)
  const [monthIndex, setMonthIndex] = useState (null)
  const [value, setValue] = useState (0)
  const [description, setDescription] = useState ('')
  const [dueDate, setDueDate] = useState (null)
  const [paid, setPaid] = useState (false)
  const [supplier, setSupplier] = useState ('')
  const [documentNumber, setDocumentNumber] = useState ('')
  const [issueDate, setIssueDate] = useState (null)
  const [subaccount, setSubaccount] = useState ('')
  const [costCenter, setCostCenter] = useState ('')
  const [entryType, setEntryType] = useState ('')
  const [creatingCategory, setCreatingCategory] = useState (false)
  const [newCategoryName, setNewCategoryName] = useState ('')
  const [newCategoryKind, setNewCategoryKind] = useState (KIND_OPTIONS[0])
  const [submitting, setSubmitting] = useState (false)
  const [errorMessage, setErrorMessage] = useState (null)

  const monthOptions = useMemo (() => Array.from ({ length: 12 }, (_, index) => ({
    label: DateTime.local (Number (year), index + 1, 1).setLocale ('pt-BR').toFormat ('LLLL'),
    value: index
  })), [year])

  useEffect (() => {
    if (!visible) return

    setErrorMessage (null)
    setCreatingCategory (false)
    setNewCategoryName ('')
    setCategory (expense?.category ? categoryOption (expense.category) : null)
    setMonthIndex (expense ? Number (expense.competence_month.slice (5, 7)) - 1 : DateTime.now ().month - 1)
    setValue (expense?.value ?? 0)
    setDescription (expense?.description ?? '')
    setDueDate (expense?.due_date ?? null)
    setPaid (expense?.paid ?? false)
    setSupplier (expense?.supplier ?? '')
    setDocumentNumber (expense?.document_number ?? '')
    setIssueDate (expense?.issue_date ?? null)
    setSubaccount (expense?.subaccount ?? '')
    setCostCenter (expense?.cost_center ?? '')
    setEntryType (expense?.entry_type ?? '')
  }, [visible, expense])

  const submit = async event => {
    event?.preventDefault ()

    try {
      setSubmitting (true)
      setErrorMessage (null)

      let selectedCategory = category?.value

      if (creatingCategory) {
        if (!newCategoryName.trim ()) return setErrorMessage ('Informe o nome da categoria')

        const { data: created } = await cutileiApi.post (
          `/businesses/${businessId}/dre_expense_categories`,
          { name: newCategoryName, kind: newCategoryKind.value },
          requestAuth
        )

        selectedCategory = created
        handleCategoryCreated (created)
      }

      if (!selectedCategory) return setErrorMessage ('Escolha uma categoria')
      if (!(Number (value) > 0)) return setErrorMessage ('Informe um valor maior que zero')

      const payload = {
        categoryId: selectedCategory.id,
        competenceMonth: DateTime.local (Number (year), (monthIndex ?? 0) + 1, 1).toISODate (),
        value,
        description,
        dueDate,
        supplier,
        documentNumber,
        issueDate,
        subaccount,
        costCenter,
        entryType,
        paid,
        ...(paid && expense?.paid_at && { paidAt: expense.paid_at })
      }

      const { data: saved } = expense
        ? await cutileiApi.put (`/businesses/${businessId}/dre_expenses/${expense.id}`, payload, requestAuth)
        : await cutileiApi.post (`/businesses/${businessId}/dre_expenses`, payload, requestAuth)

      handleConfirm (saved)
      handleClose ()
    } catch (error) {
      logApiError (error)
      setErrorMessage (getApiErrorMessage (error))
    } finally {
      setSubmitting (false)
    }
  }

  return (
    <Dialog open={visible} onOpenChange={open => { if (!open) handleClose() }}>
      <DialogContent size='md' closeOnOutsideClick={false} aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{expense ? 'Editar despesa' : 'Nova despesa'}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Form id='dre-expense-form' onSubmit={submit}>
            {creatingCategory ? (
              <>
                <Row>
                  <FormField>
                    <Label>Nova categoria</Label>
                    <Input
                      placeholder='Nome (ex.: Bônus, Água, Luz)'
                      value={newCategoryName}
                      onChange={event => setNewCategoryName (event.target.value)}
                    />
                  </FormField>
                  <FormField>
                    <Label>Tipo</Label>
                    <Select
                      name='kind'
                      placeholder='Tipo...'
                      value={newCategoryKind}
                      options={KIND_OPTIONS}
                      onChange={(_, option) => setNewCategoryKind (option)}
                    />
                  </FormField>
                </Row>
                <LinkButton type='button' onClick={() => setCreatingCategory (false)}>
                  Usar categoria existente
                </LinkButton>
              </>
            ) : (
              <>
                <Row>
                  <FormField>
                    <Label>Categoria</Label>
                    <Select
                      name='category'
                      placeholder='Categoria...'
                      value={category}
                      options={categories.map (categoryOption)}
                      onChange={(_, option) => setCategory (option)}
                    />
                  </FormField>
                </Row>
                <LinkButton type='button' onClick={() => setCreatingCategory (true)}>
                  + Nova categoria
                </LinkButton>
              </>
            )}
            <Row>
              <FormField>
                <Label>Mês de competência</Label>
                <Select
                  name='month'
                  placeholder='Mês...'
                  value={monthOptions.find (option => option.value === monthIndex) ?? null}
                  options={monthOptions}
                  onChange={(_, option) => setMonthIndex (option.value)}
                />
              </FormField>
              <FormField>
                <Label>Valor</Label>
                <MoneyInput
                  name='value'
                  placeholder='Valor'
                  value={value}
                  onValueChange={(_, newValue) => setValue (newValue)}
                />
              </FormField>
            </Row>
            <Row>
              <FormField>
                <Label>Vencimento (opcional)</Label>
                <DateInput
                  value={dueDate}
                  placeholder='Sem vencimento'
                  clearable
                  onChange={setDueDate}
                />
              </FormField>
              <FormField>
                <CheckboxLabel>
                  <CheckBox value={paid} onValueChange={setPaid}/>
                  Já foi paga
                </CheckboxLabel>
              </FormField>
            </Row>
            <Row>
              <FormField>
                <Input
                  placeholder='Descrição (opcional)'
                  value={description}
                  onChange={event => setDescription (event.target.value)}
                />
              </FormField>
            </Row>
            <Row>
              <FormField>
                <Label>Fornecedor (opcional)</Label>
                <Input
                  placeholder='Fornecedor'
                  value={supplier}
                  onChange={event => setSupplier (event.target.value)}
                />
              </FormField>
              <FormField>
                <Label>Número (opcional)</Label>
                <Input
                  placeholder='Número do documento'
                  value={documentNumber}
                  onChange={event => setDocumentNumber (event.target.value)}
                />
              </FormField>
            </Row>
            <Row>
              <FormField>
                <Label>Emissão (opcional)</Label>
                <DateInput
                  value={issueDate}
                  placeholder='Sem emissão'
                  clearable
                  onChange={setIssueDate}
                />
              </FormField>
              <FormField>
                <Label>Subconta (opcional)</Label>
                <Input
                  placeholder='Ex.: Salário Atendente 1'
                  value={subaccount}
                  onChange={event => setSubaccount (event.target.value)}
                />
              </FormField>
            </Row>
            <Row>
              <FormField>
                <Label>Centro de custo (opcional)</Label>
                <Input
                  placeholder='Ex.: LOJA'
                  value={costCenter}
                  onChange={event => setCostCenter (event.target.value)}
                />
              </FormField>
              <FormField>
                <Label>Tipo (opcional)</Label>
                <Input
                  placeholder='Ex.: DESPESA'
                  value={entryType}
                  onChange={event => setEntryType (event.target.value)}
                />
              </FormField>
            </Row>
            {errorMessage && (
              <ErrorContainer>
                <InfoText>{errorMessage}</InfoText>
              </ErrorContainer>
            )}
          </Form>
        </DialogBody>
        <DialogFooter>
          <Button type='submit' form='dre-expense-form' disabled={submitting}>
            {submitting ? <ButtonLoading/> : expense ? 'Salvar despesa' : 'Adicionar despesa'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
